<template>
	<v-autocomplete
		id="generic-timezone-picker"
		v-bind="$attrs"
		:model-value="modelValue"
		:items="timezones"
		:rules="rules"
		:hide-details="hideDetails"
		:density="density"
		item-title="formatted"
		item-value="original"
		no-data-text="No timezones found."
		@update:model-value="onUpdate">
		<!-- Bold the trailing (Required)/(Optional) tag on the label (LSP-16777). -->
		<template v-if="label" #label><FieldLabel :label="label" /></template>
	</v-autocomplete>
</template>

<script setup lang="ts">
	/*
	 * TimezonePicker.vue
	 * A v-autocomplete for selecting an IANA timezone, backed by the shared
	 * getTimezones() helper.
	 *
	 * This is the ORGANISATION timezone (settings + welcome wizard), and its list is deliberately NOT the
	 * scheduling panel's. Since LSP-17619 the Schedule Call picker uses getSchedulingTimezones(), which
	 * additionally drops EST/HST/MST/ROC — absent from java.time, so contact-mobile cannot seed a picker
	 * from them. Those four must stay OFFERED here: an org already storing one would otherwise render a
	 * blank selection. Do not "unify" the two lists; see SCHEDULING_EXCLUDED_TIMEZONES in
	 * ianaTimezoneOptions.ts for why they differ.
	 */
	import { getTimezones } from "@/helpers/ianaTimezoneOptions";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	defineProps<{
		modelValue?: string | null;
		label?: string;
		rules?: Array<(value: string | null | undefined) => boolean | string>;
		hideDetails?: boolean | "auto";
		density?: "default" | "comfortable" | "compact";
	}>();

	const emit = defineEmits<{
		(event: "update:modelValue", value: string | null): void;
	}>();

	const timezones = getTimezones();

	function onUpdate(value: string | null): void {
		emit("update:modelValue", value);
	}
</script>
